import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Loader2, PauseCircle, PlayCircle, ShieldCheck } from "lucide-react";
import {
  errorMessage,
  fetchGuard,
  formatDateTime,
  formatNumber,
  formatRate,
  pauseSending,
  resumeSending,
  type GuardStatus,
} from "@/lib/comms";
import { ErrorState } from "./shared";

const GUARD_QUERY_KEY = ["/api/admin/comms/guard"];

/** One shared 30-second poll: the banner and the Settings panel read the same cache entry. */
export function useGuardStatus() {
  return useQuery<GuardStatus>({
    queryKey: GUARD_QUERY_KEY,
    queryFn: fetchGuard,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

function invalidateGuard() {
  queryClient.invalidateQueries({ queryKey: GUARD_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/settings"] });
  queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/overview"] });
}

function useResume(onDone?: () => void) {
  const { toast } = useToast();
  return useMutation({
    mutationFn: () => resumeSending(),
    onSuccess: () => {
      invalidateGuard();
      onDone?.();
      toast({ title: "Sending resumed", description: "The worker will pick the queue back up on its next pass." });
    },
    onError: (e: unknown) => toast({ title: "Could not resume sending", description: errorMessage(e), variant: "destructive" }),
  });
}

/**
 * Sits above every tab of the console. While the circuit breaker holds the queue there is
 * nothing more important on the page, so it is loud and it is first.
 */
export function CommsPausedBanner() {
  const { data } = useGuardStatus();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const resumeMutation = useResume(() => setConfirmOpen(false));

  if (!data?.paused) return null;

  return (
    <>
      <div
        className="rounded-md border border-destructive bg-destructive/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
        data-testid="banner-comms-paused"
      >
        <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-destructive">Sending is paused. Nothing is going out.</p>
          <p className="text-sm text-destructive/90 break-words">
            {data.paused.reason || "No reason recorded."} · paused {formatDateTime(data.paused.at)}
          </p>
        </div>
        <Button
          variant="destructive"
          className="shrink-0"
          onClick={() => setConfirmOpen(true)}
          data-testid="button-resume-sending"
        >
          <PlayCircle className="w-4 h-4 mr-2" /> Resume sending
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resume sending?</AlertDialogTitle>
            <AlertDialogDescription>
              The queue starts draining again immediately, including everything that piled up while it was held.
              {data.breach
                ? ` The condition that stopped it is still true: ${data.breach}. It will trip again unless you have fixed the cause.`
                : " Make sure whatever tripped the breaker has actually been dealt with."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it paused</AlertDialogCancel>
            <AlertDialogAction onClick={() => resumeMutation.mutate()} data-testid="button-confirm-resume-sending">
              {resumeMutation.isPending ? "Resuming…" : "Resume sending"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "danger" }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold ${tone === "danger" ? "text-destructive" : ""}`}>{value}</p>
      {sub ? <p className="text-xs text-muted-foreground leading-snug">{sub}</p> : null}
    </div>
  );
}

export default function GuardPanel() {
  const { toast } = useToast();
  const { data, isLoading, error } = useGuardStatus();
  const [pauseOpen, setPauseOpen] = useState(false);
  const [reason, setReason] = useState("");
  const resumeMutation = useResume();

  const pauseMutation = useMutation({
    mutationFn: () => pauseSending(reason),
    onSuccess: () => {
      invalidateGuard();
      setPauseOpen(false);
      setReason("");
      toast({ title: "Sending paused", description: "The worker stops until you resume it. Nothing already sent is affected." });
    },
    onError: (e: unknown) => toast({ title: "Could not pause sending", description: errorMessage(e), variant: "destructive" }),
  });

  const paused = !!data?.paused;
  const breach = data?.breach || null;

  return (
    <Card className={paused ? "border-destructive" : breach ? "border-amber-400 dark:border-amber-700" : ""}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Circuit breaker
            </CardTitle>
            <CardDescription>
              Checked continuously against the last 24 hours. If the bounce or complaint rate crosses a threshold on a big enough sample, or the
              queue stalls, sending stops on its own and Telegram is told. Refreshes every 30 seconds.
            </CardDescription>
          </div>
          <div className="flex gap-2 shrink-0">
            {paused ? (
              <Button type="button" onClick={() => resumeMutation.mutate()} disabled={resumeMutation.isPending} data-testid="button-guard-resume">
                {resumeMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                Resume sending
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={() => setPauseOpen(true)} data-testid="button-guard-pause">
                <PauseCircle className="w-4 h-4 mr-2" /> Pause sending
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <ErrorState error={error} />
        ) : isLoading || !data ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
        ) : (
          <>
            {paused ? (
              <p className="text-sm text-destructive flex items-start gap-2" data-testid="text-guard-paused">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                Paused {formatDateTime(data.paused!.at)}. {data.paused!.reason || "No reason recorded."}
              </p>
            ) : null}

            {breach ? (
              <p className="text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2" data-testid="text-guard-breach">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                {breach}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="text-guard-clear">
                Nothing is over the line right now.
              </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Tile label="Sent, last 24h" value={formatNumber(data.last24h.sent)} sub="Everything the worker put out" />
              <Tile
                label="Bounced"
                value={formatRate(data.last24h.bounceRate)}
                sub={`${formatNumber(data.last24h.bounced)} messages`}
                tone={breach && /bounce/i.test(breach) ? "danger" : undefined}
              />
              <Tile
                label="Complained"
                value={formatRate(data.last24h.complaintRate)}
                sub={`${formatNumber(data.last24h.complained)} messages`}
                tone={breach && /complaint/i.test(breach) ? "danger" : undefined}
              />
              <Tile
                label="Queue due"
                value={formatNumber(data.queue.due)}
                sub={
                  data.queue.oldestDueMinutes === null || data.queue.oldestDueMinutes === undefined
                    ? "Nothing waiting"
                    : `Oldest waiting ${formatNumber(data.queue.oldestDueMinutes)} min`
                }
                tone={breach && /stall|queue/i.test(breach) ? "danger" : undefined}
              />
            </div>
          </>
        )}
      </CardContent>

      <AlertDialog open={pauseOpen} onOpenChange={setPauseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pause all sending?</AlertDialogTitle>
            <AlertDialogDescription>
              The worker stops. Campaigns, journeys and automations keep queueing, but nothing leaves until you resume. Use this the moment
              something looks wrong.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="pause-reason">Reason (optional)</Label>
            <Input
              id="pause-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Investigating a spike in bounces from a bad import"
              data-testid="input-pause-reason"
            />
            <p className="text-xs text-muted-foreground">Whoever sees the banner next will read this.</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReason("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={e => { e.preventDefault(); pauseMutation.mutate(); }}
              data-testid="button-confirm-pause"
            >
              {pauseMutation.isPending ? "Pausing…" : "Pause sending"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
